import { UIKit } from "@/shared/ui";
/**
 * Виджет «Горячие клавиши»: кнопка со знаком вопроса в тулбаре и всплывающая
 * панель со списком клавиш. Нужен, чтобы пользователь вообще узнал, что редактором
 * можно управлять с клавиатуры, — иначе про V, W и Tab никто не догадается.
 *
 * Открывается кликом или клавишей «?», закрывается кликом мимо, Esc или повторным «?».
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { HOTKEY_GROUPS } from "@/widgets/editor-hotkeys";

/** Клавиша в подсказке — маленькая «кнопка», как принято в справках по шорткатам. */
const Key = ({ children }: { children: string }) => (
	<kbd className="rounded-md border border-border bg-slate-50 px-1.5 py-px font-sans text-xs text-ink shadow-sm">
		{children}
	</kbd>
);

export const HotkeysHelp = () => {
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState<CSSProperties | null>(null);
	const root = useRef<HTMLDivElement>(null);
	const dialog = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement | null;
			if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
			// «?» на любой раскладке: физически это Shift + «/» или Shift + «,»
			if (e.key === "?" || (e.shiftKey && (e.code === "Slash" || e.code === "Comma"))) {
				e.preventDefault();
				setOpen((v) => !v);
			} else if (e.key === "Escape") {
				setOpen(false);
			}
		};
		const onPointerDown = (e: PointerEvent) => {
			const target = e.target as Node;
			if (!root.current?.contains(target) && !dialog.current?.contains(target)) setOpen(false);
		};
		window.addEventListener("keydown", onKey);
		window.addEventListener("pointerdown", onPointerDown);
		return () => {
			window.removeEventListener("keydown", onKey);
			window.removeEventListener("pointerdown", onPointerDown);
		};
	}, []);

	useEffect(() => {
		if (!open) return;
		const updatePosition = () => {
			const bounds = root.current?.getBoundingClientRect();
			if (!bounds) return;
			setPosition({ right: window.innerWidth - bounds.right, top: bounds.bottom + 6 });
		};
		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);
		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [open]);

	return (
		<div ref={root} className="relative">
			<UIKit.Tooltip content="Горячие клавиши (?)" side="left">
				<UIKit.Button
					className={`flex size-8 items-center justify-center border-transparent bg-transparent p-0 text-ink hover:border-transparent hover:bg-slate-100 hover:text-ink${
						open ? " border-[#cfe0fd] bg-accent-soft text-accent" : ""
					}`}
					aria-label="Горячие клавиши"
					aria-expanded={open}
					onClick={() => setOpen((v) => !v)}
				>
					<UIKit.IconHelp />
				</UIKit.Button>
			</UIKit.Tooltip>

			{open &&
				position !== null &&
				createPortal(
					<div
						ref={dialog}
						role="dialog"
						aria-label="Горячие клавиши"
						className="fixed z-100 max-h-hotkeys w-85 max-w-viewport-gutter overflow-y-auto rounded-lg border border-border bg-surface p-3.5 shadow-xl"
						style={position}
					>
						<div className="mb-2 flex items-baseline justify-between">
							<UIKit.Text className="text-sm font-semibold text-ink">Горячие клавиши</UIKit.Text>
							<UIKit.Text as="span" className="text-xs text-ink">
								на Mac Ctrl = ⌘
							</UIKit.Text>
						</div>
						<div className="flex flex-col gap-3">
							{HOTKEY_GROUPS.map((group) => (
								<div key={group.title}>
									<h3 className="mb-1 text-xs font-semibold tracking-label text-ink uppercase">
										{group.title}
									</h3>
									<ul className="flex flex-col gap-1">
										{group.items.map((item) => (
											<li
												key={item.action}
												className="flex items-center justify-between gap-3 text-xs text-ink"
											>
												<UIKit.Text as="span">{item.action}</UIKit.Text>
												<UIKit.Text as="span" className="flex shrink-0 items-center gap-1">
													{item.keys.map((key, i) => (
														<UIKit.Text as="span" key={key} className="flex items-center gap-1">
															{i > 0 && (
																<UIKit.Text as="span" className="text-xs text-ink">
																	+
																</UIKit.Text>
															)}
															<Key>{key}</Key>
														</UIKit.Text>
													))}
												</UIKit.Text>
											</li>
										))}
									</ul>
								</div>
							))}
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
};
